import { useState, useEffect, useMemo } from 'react';
import { Alert, Typography, Slide, Stack } from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import {
  getMaintenanceFeaturesLabels,
  processMaintenanceInfo,
  useMaintenanceInfo,
} from '@features/admin/adminAPI';
import { selectShowMaintenanceBanner } from '@features/view/viewSlice';
import {
  selectCanRemoteAccessController,
  selectCanRemoteAccessLeo,
} from '@features/auth/authSlice';

const ScheduledMaintenanceBanner = () => {
  const canRemoteAccessLeo = useAppSelector(selectCanRemoteAccessLeo);
  const canRemoteAccessController = useAppSelector(
    selectCanRemoteAccessController,
  );

  const { data } = useMaintenanceInfo();
  const info = useMemo(() => {
    if (data) return processMaintenanceInfo(data);
    return null;
  }, [data]);
  const isLeoCloudMaintenance = info?.isLeoCloudMaintenance ?? false;
  const isTerminalModeMaintenance = info?.isTerminalModeMaintenance ?? false;

  const showMaintenanceBanner = useAppSelector(selectShowMaintenanceBanner);

  // const [shownAndClosed, setShowAndClosed] = useSessionStorageState(
  //   `shownMaintenanceWarning-${data ? data.start_date : 'unknown'}`,
  //   { defaultValue: false },
  // );

  // I think we should show them everytime they load the site or refresh so they don't forget
  const [shownAndClosed, setShowAndClosed] = useState(false);
  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setShowAndClosed(true);
    setOpen(false);
  };

  const hasPermissionsForFeatureUnderMaintenance = useMemo(() => {
    return (
      isLeoCloudMaintenance ||
      (isTerminalModeMaintenance &&
        (canRemoteAccessLeo || canRemoteAccessController))
    );
  }, [
    canRemoteAccessController,
    canRemoteAccessLeo,
    isLeoCloudMaintenance,
    isTerminalModeMaintenance,
  ]);

  useEffect(() => {
    if (
      showMaintenanceBanner &&
      !shownAndClosed &&
      hasPermissionsForFeatureUnderMaintenance
    ) {
      setOpen(true);
    }
  }, [
    showMaintenanceBanner,
    shownAndClosed,
    hasPermissionsForFeatureUnderMaintenance,
  ]);

  const formattedMaintenanceInterval = useMemo(() => {
    if (data) {
      const info = processMaintenanceInfo(data);
      if (info) return info.formattedMaintenanceInterval;
    }
    return 'Unknown';
  }, [data]);

  const reason = useMemo(() => {
    if (data?.reason) return data.reason;
    return 'We are performing scheduled maintenance to improve our services and enhance your experience.';
  }, [data]);

  const featuresAffected = useMemo(() => {
    if (data?.features) return getMaintenanceFeaturesLabels(data.features);

    return 'LEO Cloud';
  }, [data]);

  return (
    <Slide in={open}>
      <Alert
        variant="filled"
        onClose={handleClose}
        severity="warning"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 3,
          boxShadow: (theme) => theme.shadows[10],
        }}
        slotProps={{
          message: {
            sx: {
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            },
          },
        }}
      >
        <Stack alignItems={'center'}>
          <Typography variant="h6">Scheduled Maintenance Notice</Typography>
          <Stack direction={'row'} columnGap={1}>
            <Typography
              sx={{
                display: 'flex',
                gap: 0.5,
              }}
            >
              <strong>Date:</strong>
              {formattedMaintenanceInterval}
            </Typography>
          </Stack>
          <Typography>{reason}</Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            During this time {featuresAffected} will be temporarily unavailable.
            We apologize for any inconvenience.
          </Typography>
        </Stack>
      </Alert>
    </Slide>
  );
};

export default ScheduledMaintenanceBanner;
